import React from "react";
import { Panel, SectionGrid, SummaryGrid } from "../AdminAnalyticsCards";
import CssBarChart from "../charts/CssBarChart";
import AdminAnalyticsTable from "../tables/AdminAnalyticsTable";
import { aa, asArray, asNumber, formatDay, formatLabel, formatNumber, formatRate } from "../adminAnalyticsUtils";
function formatTelemetryLabel(value) {
  const text = String(value || "").trim();
  if (!text) return aa("unknown", "Unknown");
  if (text.toLowerCase() === "sst") return "SST";
  return formatLabel(text);
}
function formatFeatureLabel(row) {
  return `${formatTelemetryLabel(row?.featureFamily)} / ${formatTelemetryLabel(row?.featureName)}`;
}
function formatUseCaseLabel(row) {
  return `${formatFeatureLabel(row)} / ${formatTelemetryLabel(row?.environment)} / ${formatTelemetryLabel(row?.tripPhase)}`;
}
function formatRequestCountDetail(count) {
  return aa("request_count_detail", "{{count}} requests", {
    count: formatNumber(count),
  });
}
function formatProviderAttemptSublabel(providerAttempts, failures) {
  return aa("provider_attempts_failures_sublabel", "{{providerAttempts}} provider attempts, {{failures}} failures", {
    providerAttempts: formatNumber(providerAttempts),
    failures: formatNumber(failures),
  });
}
function formatFeatureRequestRateSublabel(rate) {
  return aa("feature_request_rate_sublabel", "{{rate}} of feature requests", {
    rate: formatRate(rate),
  });
}
function formatCacheFailureSublabel(cacheHitRate, failureRate) {
  return aa("cache_failure_sublabel", "{{cacheHitRate}} cache hit, {{failureRate}} failure", {
    cacheHitRate: formatRate(cacheHitRate),
    failureRate: formatRate(failureRate),
  });
}
function formatFailureRateSublabel(rate) {
  return aa("failure_rate_sublabel", "{{rate}} failure", {
    rate: formatRate(rate),
  });
}
function makeUseCaseKey(row, index, suffix = "") {
  return [row?.route, row?.featureFamily, row?.featureName, row?.environment, row?.tripPhase, row?.providerName, row?.country, row?.region, suffix, index].filter(part => part !== undefined && part !== null && part !== "").join("|");
}
export default function AdminAnalyticsApiCacheTab({
  data
}) {
  const summary = data?.summary || {};
  const fallback = data?.fallbackSummary || {};
  const forceRefreshByUseCase = asArray(data?.forceRefreshByUseCase);
  const hasForceRefreshUsage = forceRefreshByUseCase.some(row => asNumber(row?.forceRefreshCount) > 0);
  return <>
      <SummaryGrid cards={[{
      label: aa("feature_requests", "Feature requests"),
      value: formatNumber(summary.totalFeatureRequests)
    }, {
      label: aa("provider_attempts", "Provider attempts"),
      value: formatNumber(summary.totalProviderAttempts)
    }, {
      label: aa("cache_hits", "Cache hits"),
      value: formatNumber(summary.cacheHits)
    }, {
      label: aa("live_fetches", "Live fetches"),
      value: formatNumber(summary.liveFetches)
    }, {
      label: aa("cache_hit_rate", "Cache hit rate"),
      value: formatRate(summary.cacheHitRate)
    }, {
      label: aa("force_refresh_rate", "Force refresh rate"),
      value: formatRate(summary.forceRefreshRate),
      detail: formatRequestCountDetail(summary.forceRefreshRequests)
    }, {
      label: aa("successful_requests", "Successful requests"),
      value: formatNumber(summary.successfulFeatureRequests)
    }, {
      label: aa("failed_requests", "Failed requests"),
      value: formatNumber(summary.failedFeatureRequests)
    }, {
      label: aa("fallback_groups", "Fallback groups"),
      value: formatNumber(summary.fallbackRequestGroups),
      detail: aa("requests_with_multiple_provider_attempts", "Requests with multiple provider attempts")
    }]} />


      <SectionGrid>
        <CssBarChart title={aa("requests_by_day", "Requests by day")} rows={data?.requestsByDay} getLabel={row => formatDay(row?.day)} getValue={row => row?.featureRequests} getSublabel={row => formatProviderAttemptSublabel(row?.providerAttempts, row?.failures)} />

        <CssBarChart title={aa("cache_results", "Cache results")} rows={data?.cacheResults} getLabel={row => formatLabel(row?.cacheResult)} getValue={row => row?.count} getSublabel={row => formatFeatureRequestRateSublabel(row?.rate)} />

        <CssBarChart title={aa("result_statuses", "Result statuses")} rows={data?.resultStatuses} getLabel={row => formatLabel(row?.resultStatus)} getValue={row => row?.count} getSublabel={row => formatFeatureRequestRateSublabel(row?.rate)} />

        <CssBarChart title={aa("attempt_outcomes", "Attempt outcomes")} rows={data?.attemptOutcomes} getLabel={row => formatLabel(row?.attemptOutcome)} getValue={row => row?.count} />

        <CssBarChart title={aa("feature_families", "Feature families")} rows={data?.featureFamilies} getLabel={row => formatLabel(row?.featureFamily)} getValue={row => row?.count} getSublabel={row => formatCacheFailureSublabel(row?.cacheHitRate, row?.failureRate)} />

        <CssBarChart title={aa("feature_names", "Feature names")} rows={data?.featureNames} getLabel={row => `${formatLabel(row?.featureFamily)} / ${formatLabel(row?.featureName)}`} getValue={row => row?.count} getSublabel={row => formatCacheFailureSublabel(row?.cacheHitRate, row?.failureRate)} />

        <CssBarChart title={aa("routes", "Routes")} rows={data?.routes} getLabel={row => row?.route || aa("unknown_route", "Unknown route")} getValue={row => row?.count} getSublabel={row => formatFailureRateSublabel(row?.failureRate)} />

        <CssBarChart title={aa("providers_selected", "Providers selected")} rows={data?.providersSelected} getLabel={row => row?.providerName || aa("unknown_provider", "Unknown provider")} getValue={row => row?.count} />

        <Panel title={aa("request_outcomes", "Request outcomes")}>
          <p className="admin-analytics-helper-note">{aa("fallback_groups_are_requests_where_more_than_one_provider_was_attempted", "Fallback groups are requests where more than one provider was attempted.")}

          </p>
          <p className="admin-analytics-helper-note">{aa("skipped_or_not_applicable_often_means_the_feature_was_intentionally_not_needed_f", "Skipped or not applicable often means the feature was intentionally not needed for that request, such as SST for freshwater.")}


          </p>
          <div className="admin-analytics-completion-grid admin-analytics-completion-grid--dense">
            {[[aa("partial", "Partial"), summary.partialFeatureRequests], [aa("no_data", "No data"), summary.noDataFeatureRequests], [aa("skipped", "Skipped"), summary.skippedFeatureRequests], [aa("not_applicable_cache", "Not applicable cache"), summary.notApplicableCache], [aa("provider_successes", "Provider successes"), summary.providerSuccesses], [aa("provider_errors", "Provider errors"), summary.providerErrors], [aa("provider_no_data", "Provider no data"), summary.providerNoData], [aa("fallback_attempts", "Fallback attempts"), fallback.totalFallbackProviderAttempts]].map(([label, value]) => <div className="admin-analytics-completion-item" key={label}>
                <span>{label}</span>
                <strong>{formatNumber(value)}</strong>
              </div>)}
          </div>
        </Panel>
        <CssBarChart title={aa("country_and_region_usage", "Country and region usage")} rows={data?.countryRegionUsage} getLabel={row => `${row?.country || aa("unknown", "Unknown")} / ${row?.region || aa("unknown", "Unknown")}`} getValue={row => row?.count} />

        <CssBarChart title={aa("environment_usage", "Environment usage")} rows={data?.environmentUsage} getLabel={row => formatLabel(row?.environment)} getValue={row => row?.count} />

        <CssBarChart title={aa("trip_phase_usage", "Trip phase usage")} rows={data?.tripPhaseUsage} getLabel={row => formatLabel(row?.tripPhase)} getValue={row => row?.count} />

        <AdminAnalyticsTable title={aa("cache_by_use_case", "Cache by use case")} rows={data?.cacheByUseCase} minWidth={1120} rowKey={(row, index) => makeUseCaseKey(row, index, "cache")} emptyMessage={aa("no_cache_use_case_data_in_this_period", "No cache use-case data in this period.")} columns={[{
        header: aa("use_case", "Use case"),
        render: row => formatUseCaseLabel(row)
      }, {
        header: aa("route", "Route"),
        render: row => row?.route || aa("unknown_route", "Unknown route")
      }, {
        header: aa("feature", "Feature"),
        render: row => formatFeatureLabel(row)
      }, {
        header: aa("environment", "Environment"),
        render: row => formatTelemetryLabel(row?.environment)
      }, {
        header: aa("trip_phase", "Trip phase"),
        render: row => formatTelemetryLabel(row?.tripPhase)
      }, {
        header: aa("total_requests", "Total requests"),
        render: row => formatNumber(row?.totalRequests)
      }, {
        header: aa("cache_hits", "Cache hits"),
        render: row => formatNumber(row?.cacheHits)
      }, {
        header: aa("live_fetches", "Live fetches"),
        render: row => formatNumber(row?.liveFetches)
      }, {
        header: aa("not_applicable", "Not applicable"),
        render: row => formatNumber(row?.notApplicableCache)
      }, {
        header: aa("cache_hit_rate", "Cache hit rate"),
        render: row => formatRate(row?.cacheHitRate)
      }, {
        header: aa("failure_rate", "Failure rate"),
        render: row => formatRate(row?.failureRate)
      }, {
        header: aa("force_refresh_rate", "Force refresh rate"),
        render: row => formatRate(row?.forceRefreshRate)
      }]} />

        <AdminAnalyticsTable title={aa("provider_by_use_case", "Provider by use case")} rows={data?.providerByUseCase} minWidth={980} rowKey={(row, index) => makeUseCaseKey(row, index, "provider")} emptyMessage={aa("no_provider_use_case_data_in_this_period", "No provider use-case data in this period.")} columns={[{
        header: aa("use_case", "Use case"),
        render: row => <>
                  <strong>{formatUseCaseLabel(row)}</strong>
                  <span>{row?.route || aa("unknown_route", "Unknown route")}</span>
                </>
      }, {
        header: aa("provider", "Provider"),
        render: row => row?.providerName || aa("unknown_provider", "Unknown provider")
      }, {
        header: aa("attempts", "Attempts"),
        render: row => formatNumber(row?.attempts)
      }, {
        header: aa("successes", "Successes"),
        render: row => formatNumber(row?.successes)
      }, {
        header: aa("errors", "Errors"),
        render: row => formatNumber(row?.errors)
      }, {
        header: aa("no_data", "No data"),
        render: row => formatNumber(row?.noData)
      }, {
        header: aa("success_rate", "Success rate"),
        render: row => formatRate(row?.successRate)
      }, {
        header: aa("no_data_rate", "No-data rate"),
        render: row => formatRate(row?.noDataRate)
      }, {
        header: aa("error_rate", "Error rate"),
        render: row => formatRate(row?.errorRate)
      }]} />

        <AdminAnalyticsTable title={aa("provider_by_country_region", "Provider by country/region")} rows={data?.providerByCountryRegion} minWidth={920} rowKey={(row, index) => makeUseCaseKey(row, index, "provider-country")} emptyMessage={aa("no_provider_country_region_data_in_this_period", "No provider country/region data in this period.")} columns={[{
        header: aa("provider", "Provider"),
        render: row => row?.providerName || aa("unknown_provider", "Unknown provider")
      }, {
        header: aa("country", "Country"),
        render: row => formatLabel(row?.country)
      }, {
        header: aa("region", "Region"),
        render: row => formatLabel(row?.region)
      }, {
        header: aa("attempts", "Attempts"),
        render: row => formatNumber(row?.attempts)
      }, {
        header: aa("successes", "Successes"),
        render: row => formatNumber(row?.successes)
      }, {
        header: aa("errors", "Errors"),
        render: row => formatNumber(row?.errors)
      }, {
        header: aa("no_data", "No data"),
        render: row => formatNumber(row?.noData)
      }, {
        header: aa("success_rate", "Success rate"),
        render: row => formatRate(row?.successRate)
      }, {
        header: aa("no_data_rate", "No-data rate"),
        render: row => formatRate(row?.noDataRate)
      }, {
        header: aa("error_rate", "Error rate"),
        render: row => formatRate(row?.errorRate)
      }]} />

        <AdminAnalyticsTable title={aa("cache_by_country_region", "Cache by country/region")} rows={data?.cacheByCountryRegion} minWidth={940} rowKey={(row, index) => makeUseCaseKey(row, index, "cache-country")} emptyMessage={aa("no_cache_country_region_data_in_this_period", "No cache country/region data in this period.")} columns={[{
        header: aa("country", "Country"),
        render: row => formatLabel(row?.country)
      }, {
        header: aa("region", "Region"),
        render: row => formatLabel(row?.region)
      }, {
        header: aa("feature", "Feature"),
        render: row => formatFeatureLabel(row)
      }, {
        header: aa("total_requests", "Total requests"),
        render: row => formatNumber(row?.totalRequests)
      }, {
        header: aa("cache_hits", "Cache hits"),
        render: row => formatNumber(row?.cacheHits)
      }, {
        header: aa("live_fetches", "Live fetches"),
        render: row => formatNumber(row?.liveFetches)
      }, {
        header: aa("not_applicable", "Not applicable"),
        render: row => formatNumber(row?.notApplicableCache)
      }, {
        header: aa("cache_hit_rate", "Cache hit rate"),
        render: row => formatRate(row?.cacheHitRate)
      }, {
        header: aa("failure_rate", "Failure rate"),
        render: row => formatRate(row?.failureRate)
      }]} />

        <AdminAnalyticsTable title={aa("force_refresh_by_use_case", "Force refresh by use case")} rows={hasForceRefreshUsage ? forceRefreshByUseCase : []} minWidth={760} rowKey={(row, index) => makeUseCaseKey(row, index, "force-refresh")} emptyMessage={aa("no_force_refresh_usage_in_this_period", "No force-refresh usage in this period.")} columns={[{
        header: aa("use_case", "Use case"),
        render: row => <>
                  <strong>{formatUseCaseLabel(row)}</strong>
                  <span>{row?.route || aa("unknown_route", "Unknown route")}</span>
                </>
      }, {
        header: aa("force_refresh", "Force refresh"),
        render: row => formatNumber(row?.forceRefreshCount)
      }, {
        header: aa("total_requests", "Total requests"),
        render: row => formatNumber(row?.totalRequests)
      }, {
        header: aa("force_refresh_rate", "Force refresh rate"),
        render: row => formatRate(row?.forceRefreshRate)
      }]} />

        <AdminAnalyticsTable title={aa("provider_attempts", "Provider attempts")} rows={data?.providerAttempts} rowKey={row => row?.providerName} columns={[{
        header: aa("provider", "Provider"),
        render: row => row?.providerName || aa("unknown", "Unknown")
      }, {
        header: aa("attempts", "Attempts"),
        render: row => formatNumber(row?.attempts)
      }, {
        header: aa("successes", "Successes"),
        render: row => formatNumber(row?.successes)
      }, {
        header: aa("errors", "Errors"),
        render: row => formatNumber(row?.errors)
      }, {
        header: aa("no_data", "No data"),
        render: row => formatNumber(row?.noData)
      }, {
        header: aa("success_rate", "Success rate"),
        render: row => formatRate(row?.successRate)
      }, {
        header: aa("error_rate", "Error rate"),
        render: row => formatRate(row?.errorRate)
      }]} />

        <AdminAnalyticsTable title={aa("provider_no_data_errors", "Provider no-data / errors")} rows={data?.providersWithFailures} rowKey={row => row?.providerName} columns={[{
        header: aa("provider", "Provider"),
        render: row => row?.providerName || aa("unknown", "Unknown")
      }, {
        header: aa("attempts", "Attempts"),
        render: row => formatNumber(row?.attempts)
      }, {
        header: aa("errors", "Errors"),
        render: row => formatNumber(row?.errors)
      }, {
        header: aa("no_data", "No data"),
        render: row => formatNumber(row?.noData)
      }, {
        header: aa("error_rate", "Error rate"),
        render: row => formatRate(row?.errorRate)
      }]} />

        <AdminAnalyticsTable title={aa("poor_cache_hit_features", "Poor cache hit features")} rows={data?.poorCacheHitFeatures} rowKey={row => `${row?.featureFamily}-${row?.featureName}`} columns={[{
        header: aa("feature", "Feature"),
        render: row => `${formatLabel(row?.featureFamily)} / ${formatLabel(row?.featureName)}`
      }, {
        header: aa("total", "Total"),
        render: row => formatNumber(row?.totalCount)
      }, {
        header: aa("cache_hits", "Cache hits"),
        render: row => formatNumber(row?.cacheHits)
      }, {
        header: aa("live_fetches", "Live fetches"),
        render: row => formatNumber(row?.liveFetches)
      }, {
        header: aa("cache_hit_rate", "Cache hit rate"),
        render: row => formatRate(row?.cacheHitRate)
      }]} />

        <AdminAnalyticsTable title={aa("force_refresh_by_feature", "Force refresh by feature")} rows={data?.forceRefreshByFeature} rowKey={row => `${row?.featureFamily}-${row?.featureName}`} columns={[{
        header: aa("feature", "Feature"),
        render: row => `${formatLabel(row?.featureFamily)} / ${formatLabel(row?.featureName)}`
      }, {
        header: aa("force_refresh", "Force refresh"),
        render: row => formatNumber(row?.forceRefreshCount)
      }, {
        header: aa("total", "Total"),
        render: row => formatNumber(row?.totalCount)
      }, {
        header: aa("rate", "Rate"),
        render: row => formatRate(row?.forceRefreshRate)
      }]} />

      </SectionGrid>
    </>;
}
